"use client";

import { useEffect, useState } from "react";
import type { BoardRow, BoardSource, RankHit } from "./board";

/**
 * The REAL board, from this site's /api/competition/* routes (which call World Class
 * Edge server-side). Null until the competition is live, so the page keeps its
 * pre-launch view; refreshed every minute while the page is open.
 */
export function useApiBoard(enabled = true): BoardSource | null {
  const [top, setTop] = useState<BoardRow[] | null>(null);
  // `?lb-preview=<token>` is passed through to this site's API routes, which check it
  // server-side; a wrong or missing token just means the normal (pre-launch) board.
  const [suffix, setSuffix] = useState<string | null>(null);
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("lb-preview");
    setSuffix(token ? `?preview=${encodeURIComponent(token)}` : "");
  }, []);

  useEffect(() => {
    if (!enabled || suffix === null) return;
    let alive = true;
    const load = async () => {
      try {
        const res = await fetch(`/api/competition/leaderboard${suffix}`);
        const data = (await res.json()) as { live?: boolean; top?: BoardRow[] };
        if (alive && res.ok && data.live && Array.isArray(data.top) && data.top.length) setTop(data.top);
      } catch {
        // keep whatever we have; the next tick retries
      }
    };
    load();
    const id = window.setInterval(load, 60_000);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [enabled, suffix]);

  if (!top) return null;
  return {
    top,
    // Never throws: LiveBoard has no error state, so any failure (rate limit, network)
    // shows as "not found" rather than a spinner that never stops.
    async lookup(email) {
      try {
        const res = await fetch(`/api/competition/lookup${suffix ?? ""}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        });
        if (!res.ok) return null;
        const data = (await res.json()) as { hit?: RankHit | null };
        return data.hit ?? null;
      } catch {
        return null;
      }
    },
  };
}
